/**
 * French message catalogue.
 *
 * Typed against the English catalogue, so `vue-tsc` fails when the two drift
 * apart (a key added to one but not the other).
 *
 * Typography: the strings use no-break spaces (U+00A0) before double
 * punctuation (: ? %) and between a number and its unit, as French sets them.
 * They look like plain spaces in an editor but must not be replaced by one.
 */
import type en from './en';

type MessageSchema = typeof en;

const fr: MessageSchema = {
  common: {
    loading: 'Chargement…',
    cancel: 'Annuler',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    edit: 'Modifier',
    delete: 'Supprimer',
    previous: 'Précédent',
    next: 'Suivant',
    view: 'Voir',
    noRecords: 'Aucun enregistrement',
    records: '{n} enregistrement | {n} enregistrements',
    rangeOf: '{from}–{to} sur {total}',
  },
  a11y: {
    viewMode: 'Mode d’affichage',
    sortColumn: 'Trier par {column}',
    progress: 'Progression de l’analyse',
    hoverValues: 'Utilisez les flèches pour parcourir les valeurs\u00a0; Échap efface.',
  },
  duration: {
    seconds: '{s} s',
    minutesSeconds: '{m} min {s} s',
    hours: '{h} h',
  },
  pager: {
    label: 'Pagination',
    first: 'Première page',
    previous: 'Page précédente',
    next: 'Page suivante',
    last: 'Dernière page',
    page: 'Page {n}',
  },
  tableExport: {
    selected: 'Lignes sélectionnées ({n})',
    view: 'Vue actuelle ({n})',
    all: 'Toutes les lignes correspondantes ({total})',
  },
  table: {
    id: 'ID',
    type: 'Type',
    channels: 'Canaux',
    electrodes: 'Électrodes',
    rate: 'Fréq.',
    duration: 'Durée',
    device: 'Appareil',
    created: 'Créé le',
    status: 'Statut',
    actions: 'Actions',
  },
  status: {
    running: 'En cours',
    done: 'Terminé',
    ok: 'OK',
    failed: 'Échec',
    cancelled: 'Annulé',
    skipped: 'Ignoré',
    pending: 'En attente',
  },
  titles: {
    login: 'Connexion',
    dashboard: 'Vue d’ensemble',
    explorer: 'Explorateur de données',
    run: 'Lancer une analyse',
    runs: 'Analyses',
    results: 'Résultats',
    shared: 'Jeu de données partagé',
    insights: 'Aperçus du jeu de données',
    neurocloud: 'NeuroCloud',
    notes: 'Notes',
    admin: 'Lots de données',
    fallback: 'Plateforme de recherche',
  },
  nav: {
    tagline: 'Modèle de fondation EEG',
    sections: {
      melomind: 'Melomind',
      neurocloud: 'NeuroCloud',
    },
    items: {
      dashboard: { label: 'Vue d’ensemble', hint: 'Activité récente' },
      explorer: { label: 'Explorateur', hint: 'Parcourir et sélectionner les EEG' },
      run: { label: 'Lancer une analyse', hint: 'Soumettre à REVE' },
      runs: { label: 'Analyses', hint: 'Résultats passés' },
      shared: { label: 'Jeu de données partagé', hint: 'Édition collaborative' },
      insights: { label: 'Aperçus', hint: 'Le jeu de données en un coup d’œil' },
      neurocloud: { label: 'NeuroCloud', hint: 'Marqueurs et séries calculés' },
      notes: { label: 'Notes', hint: 'Vos notes' },
      admin: { label: 'Lots de données', hint: 'Remplacer les EEG chargés' },
    },
    updates: 'Nouveautés',
    language: 'Langue',
    primaryNav: 'Navigation principale',
    skipToContent: 'Aller au contenu',
    openMenu: 'Ouvrir le menu de navigation',
    closeMenu: 'Fermer le menu de navigation',
    collapse: 'Réduire la navigation',
    expand: 'Déployer la navigation',
    signedIn: 'Connecté',
    signOut: 'Se déconnecter',
    idleWarning: 'Session bientôt expirée — bougez le curseur pour rester connecté',
    themeToLight: 'Passer au thème clair',
    themeToDark: 'Passer au thème sombre',
  },
  login: {
    tagline: 'Recherche sur le modèle de fondation EEG',
    title: 'Connexion',
    restricted: 'L’accès est réservé aux chercheurs autorisés.',
    email: 'E-mail',
    emailPlaceholder: "vous{'@'}institution.org",
    password: 'Mot de passe',
    submit: 'Se connecter',
    submitting: 'Connexion…',
    timedOut: 'Votre session a expiré après 30 minutes d’inactivité.',
    preparing: 'Chargement de votre espace de travail…',
    complianceLine1: 'Ce système traite des données de santé à caractère personnel.',
    complianceLine2:
      'Les accès sont journalisés et soumis à l’accord sur les données de votre institution.',
  },
  auth: {
    notConfigured:
      'L’authentification n’est pas configurée. Définissez VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY.',
    invalidCredentials: 'Cette combinaison d’adresse e-mail et de mot de passe n’est pas reconnue.',
    signInFailed: 'Connexion impossible pour le moment.',
  },
  dashboard: {
    eyebrow: 'Vue d’ensemble',
    title: 'Espace de travail de recherche',
    stats: {
      eegRecords: 'Enregistrements EEG',
      sessions: 'Sessions',
      participants: 'Participants',
      surveyResults: 'Résultats de questionnaires',
    },
    mlNotReady: 'Le service d’inférence REVE n’est pas prêt.',
    mlNotReadyDetail: 'Les tâches resteront en file d’attente jusqu’à ce qu’il soit disponible.',
    recentRuns: 'Analyses récentes',
    viewAll: 'Tout voir',
    open: 'ouvrir',
    noRunsYet: 'Aucune analyse pour le moment.',
    sharedDataset: 'Jeu de données partagé',
    links: {
      explore: {
        title: 'Explorer les données',
        hint: 'Parcourir et sélectionner des enregistrements EEG',
      },
      run: { title: 'Lancer une analyse', hint: 'Extraire des embeddings REVE' },
      manage: {
        title: 'Gérer le jeu de données',
        hint: 'Modifier ensemble les enregistrements partagés',
      },
      neurocloud: {
        title: 'Analyses NeuroCloud',
        hint: 'Les marqueurs et séries calculés par myBrain',
      },
    },
  },
  nc: {
    eyebrow: 'Analyses dérivées',
    title: 'NeuroCloud',
    intro:
      'L’analyse des enregistrements Melomind réalisée par myBrain : marqueurs calculés, courbes fenêtrées, statistiques de groupe et signal prétraité — importés en lecture seule aux côtés des données brutes dont ils dérivent.',
    tabsAria: 'Sections NeuroCloud',
    tabs: {
      overview: 'Vue d’ensemble',
      markers: 'Marqueurs',
      series: 'Séries temporelles',
      groups: 'Stats de groupe',
      preprocessed: 'EEG prétraités',
    },
    overview: {
      computedValues: 'Valeurs calculées',
      windowed: 'Séries fenêtrées',
      preprocessed: 'EEG prétraités',
      groupStats: 'Statistiques de groupe',
      users: 'Utilisateurs couverts',
      coverage:
        'NeuroCloud a prétraité {n} enregistrements distincts — {batch} d’entre eux sont dans le lot actuellement chargé.',
      sessionNote:
        'Les numéros de session de ce jeu de données sont des ordinaux par utilisateur (#0, #1, #2…). Ils ne référencent pas la table sessions.',
      versions: 'Versions du pipeline',
    },
    markers: {
      filterName: 'Filtrer par marqueur…',
      filterUser: 'Id utilisateur',
      user: 'Utilisateur',
      session: 'Session',
      name: 'Marqueur',
      value: 'Valeur',
    },
    series: {
      pickUser: 'Id utilisateur',
      userSelect: 'Choisir un utilisateur…',
      sessionsCount: '{n} session | {n} sessions',
      load: 'Charger les sessions',
      session: 'Session',
      empty: 'Aucune série fenêtrée pour cet utilisateur.',
      hint: 'Choisissez un utilisateur dans la liste (ou saisissez un id) pour parcourir ses courbes de marqueurs.',
      pick: 'Choisissez une ou plusieurs séries à gauche.',
    },
    export: {
      selectRow: 'Sélectionner la ligne {id}',
      clearSelection: 'Vider la sélection ({n})',
    },
    groups: {
      search: 'Rechercher un test…',
      group: 'Groupe',
      name: 'Test',
      data: 'Résultat',
    },
    pre: {
      onlyLoaded: 'Uniquement le lot chargé',
      ingest: 'Enregistrement brut',
      element: 'Segment',
      batch: 'Lot',
      inBatch: 'dans le lot',
      otherBatch: 'autre lot',
      header: 'En-tête',
      outOfBatch:
        'Cet enregistrement est dans un autre lot — changez de lot pour travailler avec son signal brut.',
      addToSelection: 'Ajouter l’enregistrement brut à la sélection',
      inSelection: 'Dans la sélection ✓',
    },
  },
  explorer: {
    eyebrow: 'Parcourir et sélectionner',
    title: 'Explorateur de données',
    intro:
      'Cliquez sur un type d’enregistrement pour le déployer, puis cliquez sur des enregistrements pour les ajouter à votre sélection.',
    views: {
      canvas: 'Canevas',
      table: 'Tableau',
      timeline: 'Chronologie',
    },
    enterFullscreen: 'Passer en plein écran',
    enterFullscreenHint: 'Étendre l’explorateur en plein écran',
    exitFullscreen: 'Quitter le plein écran',
    exitFullscreenHint: 'Quitter le plein écran (Échap)',
    runAnalysis: 'Lancer l’analyse ({n})',
    rootNode: 'Jeu de données EEG',
    unknownMontage: 'montage inconnu',
    typeFilter: 'Type : {type}',
    allRecords: 'Tous les enregistrements',
    clearFilter: 'effacer le filtre ×',
    sourceFilterLabel: 'Filtrer par source de données',
    sources: {
      melomaind: 'Melomaind',
      ilab: 'Ilab',
    },
    selectedCount: '{n} enregistrement sélectionné | {n} enregistrements sélectionnés',
    signalTotal: '· {duration} de signal',
    mixedMontages:
      '⚠ Montages mixtes ({n}). REVE encode la position des électrodes : les embeddings issus de montages différents ne sont pas directement comparables.',
    clearSelection: 'Vider la sélection',
    resetSelection: 'Réinitialiser la sélection ({n})',
    resetView: 'Réinitialiser la vue',
    showIds: 'Voir les ID ▾',
    hideIds: 'Masquer les ID ▴',
    removeId: 'Retirer #{id} de la sélection',
    export: {
      selected: 'Enregistrements sélectionnés ({n})',
      view: 'Vue actuelle ({n})',
      type: 'Tous les {type} ({total})',
      all: 'Tous les enregistrements ({total})',
    },
  },
  recordsTable: {
    selectRecord: 'Sélectionner l’enregistrement {id}',
    selectPage: 'Sélectionner la page',
  },
  updates: {
    eyebrow: 'Mises à jour produit',
    title: 'Nouveautés',
    sections: {
      before: {
        title: 'Le point de départ',
        when: 'les fondations',
        i1: 'Explorateur de données avec vues canevas, tableau et chronologie pour parcourir et sélectionner les enregistrements EEG.',
        i2: 'Analyses REVE : soumettre une sélection, suivre la progression, inspecter embeddings et résultats.',
        i3: 'Édition collaborative du jeu de données partagé, avec détection de conflits et journal d’audit complet.',
        i4: 'Graphiques d’aperçu du jeu de données et notes personnelles.',
        i5: 'Rotation des lots de données : l’extrait EEG chargé se remplace depuis l’archive MEGA privée (admin).',
        i6: 'Interface français/anglais avec thèmes clair et sombre.',
      },
      today: {
        title: 'La vague NeuroCloud',
        when: '12 août 2026',
        i1: 'NeuroCloud intégré de bout en bout : sa propre section avec vue d’ensemble, marqueurs, séries temporelles, stats de groupe et EEG prétraités — plus de 3 M de lignes d’analyses dérivées.',
        i2: 'Les enregistrements prétraités sont reliés à leur EEG brut et s’ajoutent directement à la sélection de l’explorateur.',
        i3: 'Filtre de source Melomind / Ilab dans l’explorateur, basé sur le montage d’électrodes.',
        i4: 'Revue de code complète à trois volets, chaque correction confirmée déployée — exports durcis et sécurité des rotations de lots incluses.',
        i5: 'La plateforme est passée sur braim.medigma.ai ; toutes les données sources sont archivées sur MEGA avec sommes de contrôle vérifiées.',
      },
      now: {
        title: 'Derniers raffinements',
        when: 'à l’instant',
        i1: 'Entrée valide la connexion depuis chacun des deux champs.',
        i2: 'Les tableaux NeuroCloud filtrent en direct pendant la saisie — plus besoin d’Entrée.',
        i3: 'Sélection de lignes et export CSV/JSON sur chaque tableau NeuroCloud : lignes sélectionnées, vue actuelle ou tout ce qui correspond au filtre.',
        i4: 'Navigation par numéros de page (1 2 3 …) sur tous les tableaux de l’application.',
        i5: 'L’onglet séries temporelles gagne un sélecteur d’utilisateur à côté du champ libre.',
        i6: 'Fermer la fenêtre déconnecte désormais l’utilisateur.',
        i7: 'Ce panneau « Nouveautés ».',
      },
    },
  },
  timeline: {
    loading: 'Chargement de la chronologie de collecte…',
    facetAria: 'Nombre d’enregistrements par mois pour {label}',
    otherTypes: 'Autres ({n} type) | Autres ({n} types)',
    peak: 'pic {count} · {month}',
    browse: 'parcourir →',
    empty: 'Aucune donnée de chronologie.',
  },
  run: {
    eyebrow: 'Analyse',
    title: 'Lancer REVE',
    intro:
      'Extrayez les embeddings des enregistrements sélectionnés. L’analyse se poursuit sur le serveur — vous pouvez fermer cet onglet et y revenir plus tard.',
    emptyTitle: 'Aucun enregistrement sélectionné.',
    goToExplorer: 'Aller à l’explorateur',
    selectedRecordings: 'Enregistrements sélectionnés',
    clearAll: 'Tout effacer',
    clearAllConfirm: 'Tout effacer ({n}) ?',
    clearAllHint: 'Retirer tous les enregistrements sélectionnés',
    clearAllConfirmHint: 'Cliquez de nouveau pour retirer tous les enregistrements sélectionnés',
    remove: 'retirer',
    preprocessing: 'Prétraitement',
    windowSeconds: 'Fenêtre (secondes)',
    hopSeconds: 'Pas (secondes)',
    bandLow: 'Coupure basse (Hz)',
    bandHigh: 'Coupure haute (Hz)',
    notch: 'Coupe-bande (Hz)',
    normalisation: 'Normalisation',
    normRobust: 'Z-score robuste (médiane/IQR)',
    normZscore: 'Z-score',
    normNone: 'Aucune',
    maxWindows: 'Fenêtres max par enregistrement',
    keepWindowEmbeddings: 'Conserver les embeddings par fenêtre',
    tooShort:
      '{count} des {total} enregistrements sélectionnés est probablement trop court pour une fenêtre de {window} s et sera signalé comme ignoré. | {count} des {total} enregistrements sélectionnés sont probablement trop courts pour une fenêtre de {window} s et seront signalés comme ignorés.',
    tooShortDetail:
      'Une fenêtre de {window} s à 200 Hz nécessite environ {needed} s de signal source à 250 Hz. Réduisez la longueur de fenêtre pour les inclure.',
    mixedMontages:
      'La sélection mélange {n} montages d’électrodes. REVE encode la position des électrodes : ces embeddings ne seront pas directement comparables entre eux.',
    usableCount:
      '{n} enregistrement devrait produire des embeddings | {n} enregistrements devraient produire des embeddings',
    submit: 'Démarrer l’analyse',
    submitting: 'Envoi…',
  },
  runs: {
    eyebrow: 'Historique',
    title: 'Analyses',
    intro: 'Vos analyses. Elles sont visibles uniquement par votre compte.',
    submitted: 'Soumise le',
    records: 'Enregistrements',
    batch: 'Lot',
    progress: 'Progression',
    failedCount: '({n} en échec)',
    deleteConfirm: 'Vraiment supprimer ?',
    deleteHint: 'Supprimer cette analyse et ses résultats',
    deleteConfirmHint:
      'Cliquez de nouveau pour supprimer définitivement cette analyse et ses résultats',
    emptyLead: 'Aucune analyse pour le moment.',
    emptyLink: 'Sélectionnez des enregistrements',
    emptyTail: 'pour commencer.',
  },
  results: {
    eyebrow: 'Résultats d’analyse',
    title: 'Sortie d’analyse',
    reportPdf: 'Rapport (PDF)',
    reportBuilding: 'Génération du rapport…',
    reportHint:
      'Un rapport PDF avec toutes les visualisations : projection, bandes de chaleur et profils de dimensions',
    allRuns: 'Toutes les analyses',
    processed: '{processed} sur {total} traités',
    failedInline: '· {n} en échec',
    liveHint: 'En cours sur le serveur — vous pouvez fermer cet onglet',
    tiles: {
      records: 'Enregistrements',
      embedded: 'Encodés',
      skipped: 'Ignorés',
      failed: 'Échoués',
      windows: 'Fenêtres',
    },
    embeddingDim: 'Dimension d’embedding :',
    embeddingDimTail: '— sortie de l’encodeur REVE, moyennée sur les fenêtres.',
    ingest: 'Ingestion',
    windows: 'Fenêtres',
    sourceHz: 'Hz source',
    dim: 'Dim',
    detail: 'Détail',
    embedding: 'Embedding',
    show: 'Afficher',
    hide: 'Masquer',
    embeddingFor: 'Embedding — enregistrement #{id}',
    viz: {
      heatmap: 'Carte de chaleur',
      bars: 'Barres',
      raw: 'Brut',
    },
    tabs: {
      list: 'Liste des enregistrements ({n})',
      projections: 'Projections',
    },
    report: {
      all: 'Tout',
      allHint: 'toutes les visualisations',
      heatmap: 'Carte de chaleur',
      heatmapHint: 'bandes de couleur',
      bars: 'Barres',
      barsHint: 'profils de dimensions',
      raw: 'Brut',
      rawHint: 'vecteurs numériques',
    },
    projection: {
      other: 'Autres',
      unknownType: 'sans type',
      windows: '{n} fenêtre | {n} fenêtres',
      clickHint: 'Cliquez sur un point pour ouvrir cet enregistrement dans la liste.',
    },
    searchPlaceholder: 'Filtrer les enregistrements…',
    loadingEmbeddings: 'Chargement de tous les embeddings de cette analyse…',
    needTwo: 'La projection nécessite au moins deux enregistrements encodés.',
    noResults: 'Aucun résultat produit.',
    waitingWorker: 'En attente du traitement…',
  },
  insights: {
    eyebrow: 'Données partagées',
    title: 'Aperçus du jeu de données',
    intro:
      'Le jeu de données partagé, visualisé tel qu’il est en base. Les vues d’embeddings (cartes de chaleur, projections) nécessitent des vecteurs REVE et se trouvent sur la page de résultats de chaque analyse\u00a0; voici les formes que portent les données brutes elles-mêmes.',
    eegDuration: 'Durées des enregistrements EEG',
    sessionDuration: 'Durées des sessions',
    sessionsMonthly: 'Sessions par mois',
    performanceByType: 'Performance moyenne par type d’exercice',
    surveyScores: 'Score moyen de questionnaire par mode',
    scatterTitle: 'Durée de session × performance',
    scatterHint: 'Un échantillon aléatoire de 1\u00a0500 sessions, coloré par type d’exercice.',
    scatterX: 'durée (s)',
    scatterY: 'performance',
    embeddingNote:
      'Vous cherchez les cartes de chaleur, profils de dimensions ou projections de ces données\u00a0? Elles lisent les embeddings REVE, qui n’existent qu’après une analyse — sélectionnez des enregistrements dans l’explorateur, lancez une analyse, et sa page de résultats les contient toutes.',
  },
  shared: {
    eyebrow: 'Collaboratif',
    title: 'Jeu de données partagé',
    intro:
      'La copie de travail importée. Toute l’équipe voit et modifie les mêmes enregistrements — cette vue se rafraîchit toutes les 15 secondes.',
    searchPlaceholder: 'Rechercher…',
    facetAll: 'Tous',
    notSearchable: 'Cette table n’a aucune colonne interrogeable',
    syncedAt: '· synchronisé à {time}',
    saved: 'Modification enregistrée.',
    alreadyDeleted: 'Cet enregistrement a déjà été supprimé — rien à faire ici.',
    keptTheirs: 'La version de l’autre utilisateur a été conservée.',
    editCell: 'Modifier {col} de l’enregistrement {id}',
    invalidJson: 'Cette valeur n’est pas du JSON valide — corrigez-la ou annulez la modification.',
    deleteConfirm: 'Supprimer {table} #{id} ? Un opérateur pourra le restaurer.',
    deleted: 'Enregistrement #{id} supprimé.',
    export: {
      selected: 'Lignes sélectionnées ({n})',
      view: 'Vue actuelle ({n})',
      table: 'Table entière ({total})',
      all: 'Toutes les tables',
      busy: 'Export… {mb} Mo',
      done: '{n} lignes exportées',
      selectPage: 'Sélectionner toutes les lignes de cette page',
      selectRow: 'Sélectionner la ligne {id}',
    },
  },
  conflict: {
    title: 'Quelqu’un d’autre a modifié cet enregistrement',
    body: 'Ce jeu de données est partagé. Un autre utilisateur a mis à jour cet enregistrement pendant que vous le modifiiez. Choisissez d’appliquer quand même votre modification ou de conserver la sienne.',
    field: 'Champ',
    expected: 'Valeur attendue',
    current: 'Valeur actuelle',
    attempted: 'Votre modification',
    empty: '(vide)',
    keepTheirs: 'Conserver sa version',
    overwrite: 'Appliquer ma modification quand même',
    applying: 'Application…',
  },
  notes: {
    eyebrow: 'Privé',
    title: 'Notes',
    intro: 'Visibles uniquement par votre compte.',
    new: 'Nouvelle note',
    titlePlaceholder: 'Titre',
    bodyPlaceholder: 'Écrire…',
    untitled: 'Sans titre',
    updatedAt: 'Modifiée le {date}',
    empty: 'Aucune note pour le moment.',
    deleteConfirm: 'Supprimer cette note ?',
  },
  exporter: {
    label: 'Exporter',
    nothing: 'Rien à exporter',
    hint: 'Exporter ce qui est affiché',
    exported: '{n} ligne exportée | {n} lignes exportées',
    busy: 'Export… {n} lignes',
    csvHint: 'tableur',
    jsonHint: 'fidélité intégrale',
  },
  adminBatches: {
    eyebrow: 'Super admin',
    title: 'Lots de données',
    intro:
      'La base contient un seul lot des 433 710 enregistrements du dump source à la fois. Passez au lot suivant pour travailler sur des enregistrements jamais analysés, ou rechargez un lot précédent — le fichier dump est l’archive : chaque lot peut être reproduit à l’identique. Les analyses passées et leurs résultats survivent toujours à un échange.',
    currentBatch: 'Lot actuel',
    explored: 'Lots explorés',
    remaining: '{n} jamais chargés',
    recordsLoaded: 'Enregistrements EEG chargés',
    batchGrid: 'Tous les lots',
    gridHint:
      'Cliquez sur un lot pour le sélectionner, puis chargez-le avec les boutons ci-dessous. Les numéros sont des positions dans le dump ; un même numéro contient toujours les mêmes enregistrements.',
    legend: {
      current: 'Chargé actuellement',
      explored: 'Déjà exploré',
      unexplored: 'Jamais chargé',
    },
    batchCell: 'Lot {n}',
    noSource:
      'Aucune source de lots disponible. Configurez les variables S4_* (production) ou placez le dump de 72 Gio à {path} (voir DUMP_PATH).',
    sourceS4:
      'Source des lots : bucket MEGA S4 « {bucket} » — un échange télécharge un fichier de lot d’environ 2 Go.',
    sourceDump: 'Source des lots : fichier dump local à {path}.',
    loadNext: 'Charger le lot suivant ({n})',
    loadSelected: 'Charger le lot {n}',
    revisit: 'déjà exploré — recharge exactement les mêmes enregistrements qu’avant',
    confirm: 'Cliquez à nouveau pour confirmer',
    confirmHint:
      'Cela efface les {n} enregistrements EEG actuellement chargés et charge le nouveau lot à leur place. Tous les autres utilisateurs sont bloqués jusqu’à la fin. Les résultats des analyses passées sont conservés.',
    allExplored:
      'Chaque lot a été chargé au moins une fois. Sélectionnez un lot ci-dessus pour le recharger.',
    swapWarning:
      'L’échange remplace les EEG bruts chargés par un autre lot. Les analyses passées gardent leurs résultats — seule la disponibilité du signal brut change. Les nouvelles analyses sont bloquées et les autres utilisateurs verrouillés pendant l’opération.',
    activeTitle: 'Échange en cours',
    startedBy: 'Lancé par {who} · {when}',
    status: {
      idle: 'Au repos',
      draining: 'Attente de la fin des analyses en cours',
      downloading: 'Téléchargement du lot {n} depuis le stockage objet',
      wiping: 'Effacement du lot actuel (libération du disque)',
      loading: 'Chargement du lot {n} dans la base',
      failed: 'Échec de l’échange',
    },
    runningJobs: '{n} analyse est encore en cours | {n} analyses sont encore en cours',
    pendingCancelled:
      'Les nouvelles analyses sont bloquées ; celles en file d’attente ont été annulées. L’échange démarre dès que plus rien ne tourne.',
    cancelJobs: 'Annuler les analyses en cours',
    cancelJobsConfirm: 'Vraiment les annuler ?',
    progressRows:
      '{scanned} sur {total} enregistrements du dump parcourus · {kept} retenus pour ce lot',
    progressDownload: '{mb} Mo téléchargés',
    progressCopy: '{kept} enregistrements copiés dans la base',
    usersLocked:
      'Tous les autres utilisateurs voient un écran de verrouillage jusqu’à la fin ; l’application se recharge ensuite d’elle-même partout.',
    failedTitle: 'Le dernier échange a échoué',
    retryHint:
      'Relancer est sans risque : un nouvel échange efface proprement puis recharge. Si cela se répète, consultez les journaux du backend.',
    strataTitle: 'Couverture des lots par montage',
    strataIntro:
      'Les montages rares ont moins d’enregistrements et sont donc répartis sur moins de lots. Au-delà de leur dernier lot, ils n’apparaissent plus — un enregistrement n’est jamais chargé deux fois.',
    strataRow: 'Enregistrements à {channels} électrodes — présents dans les lots 1 à {n}',
    strataRowUnknown: 'Enregistrements à montage inconnu — présents dans les lots 1 à {n}',
  },
  swapLock: {
    title: 'Échange de données en cours',
    body: 'L’administrateur remplace le lot d’EEG chargé par une autre partie du jeu de données. Votre travail est en sécurité — analyses passées, résultats et notes sont intacts.',
    status: {
      idle: 'Finalisation…',
      draining: 'Attente de la fin des analyses en cours',
      downloading: 'Récupération des nouveaux enregistrements',
      wiping: 'Effacement des enregistrements actuels',
      loading: 'Chargement des nouveaux enregistrements',
      failed: 'Finalisation…',
    },
    hint: 'Cette page se rechargera d’elle-même dès que les nouvelles données seront prêtes.',
  },
  embedding: {
    heatmapAria: 'Carte de chaleur de l’embedding, {n} dimensions',
    barsAria: 'Profil des dimensions de l’embedding, {n} valeurs signées',
    dimTooltip: 'dim {dim} · {value}',
    dimsRange: 'dims 0–{max}',
    aboveZero: 'au-dessus de zéro',
    belowZero: 'en dessous de zéro',
    scatterCaption:
      'Chaque point est l’embedding moyenné d’un enregistrement, projeté sur les deux premières composantes principales de l’analyse — PC1 {pc1} % · PC2 {pc2} % de la variance. Les points regroupés ont des embeddings similaires.',
  },
};

export default fr;
